import { useClerk, useOrganization, useOrganizationList } from "@clerk/react";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { initials } from "../lib/format";
import { CheckIcon, UpDownIcon } from "./icons";

/**
 * The sidebar's workspace card. Each machine shop is a Clerk organisation; the menu switches
 * between them and hands managing or creating one to Clerk's own dialogs.
 */
export function WorkspaceSwitcher() {
  const clerk = useClerk();
  const navigate = useNavigate();
  const { organization } = useOrganization();
  const { userMemberships, setActive } = useOrganizationList({ userMemberships: { infinite: true } });
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const name = organization?.name ?? "";
  const choose = async (orgId: string) => {
    setOpen(false);
    if (orgId === organization?.id || !setActive) return;
    await setActive({ organization: orgId });
    await navigate({ to: "/board" });
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2.5 rounded-[14px] border border-line p-2.5 text-left transition-colors hover:bg-surface"
      >
        <OrgBadge name={name} />
        <span className="flex min-w-0 grow flex-col gap-px">
          <span className="text-[11px] leading-[14px] text-muted">Workspace</span>
          <span className="truncate text-sm leading-[18px] font-bold">{name}</span>
        </span>
        <span className="text-muted">
          <UpDownIcon />
        </span>
      </button>
      {open && (
        <div role="menu" className="absolute inset-x-0 top-full z-40 mt-1.5 rounded-[14px] border border-line bg-bg p-1.5 shadow-float">
          {userMemberships.data?.map(({ organization: org }) => (
            <button
              key={org.id}
              type="button"
              role="menuitem"
              onClick={() => choose(org.id)}
              className="flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left text-sm font-semibold hover:bg-surface"
            >
              <OrgBadge name={org.name} small />
              <span className="min-w-0 grow truncate">{org.name}</span>
              {org.id === organization?.id && (
                <span className="text-brand-dark">
                  <CheckIcon />
                </span>
              )}
            </button>
          ))}
          <div className="my-1.5 border-t border-line" />
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              clerk.openOrganizationProfile();
            }}
            className="w-full rounded-md px-2 py-2 text-left text-[13px] font-semibold text-ink hover:bg-surface"
          >
            Manage workspace
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              clerk.openCreateOrganization({ afterCreateOrganizationUrl: "/board" });
            }}
            className="w-full rounded-md px-2 py-2 text-left text-[13px] font-semibold text-ink hover:bg-surface"
          >
            Create a workspace
          </button>
        </div>
      )}
    </div>
  );
}

function OrgBadge({ name, small = false }: { name: string; small?: boolean }) {
  return (
    <span
      className={`heading flex shrink-0 items-center justify-center bg-ink text-white ${small ? "size-7 rounded-sm text-[11px]" : "size-9 rounded-md text-sm leading-[18px]"}`}
    >
      {initials(name)}
    </span>
  );
}
